import { Controller } from '@hotwired/stimulus';

/**
 * attachments-field — accessible multi-file attachment picker.
 *
 * Backs a single hidden <input type=file multiple> with an "Ajouter" button and
 * a list of removable file rows. The native input replaces its FileList on every
 * pick, so we keep the accumulated set in `this.files` and rewrite the input's
 * FileList via DataTransfer on every add/remove — that way the multipart submit
 * always carries exactly the rows shown (and removing a row truly drops the file).
 *
 * Targets:
 *   - input:       the hidden <input type=file multiple>
 *   - list:        <ul> the file rows are rendered into
 *   - empty:       empty-state node (shown when no file is selected)
 *   - rowTemplate: <template> cloned per file (carries the row markup)
 *   - error:       inline notice for skipped files (too large / wrong type)
 *   - uploading:   <ul> in-flight upload skeleton rows are rendered into (auto-submit)
 *   - uploadingRowTemplate: <template> cloned per in-flight file (skeleton row markup)
 *
 * Values:
 *   - maxSize: Number — max file size in MB (0/absent = no size limit)
 *   - autoSubmit: Boolean — when true, picking a valid file submits the wrapping
 *       form immediately (no separate save button); multi-select = one grouped POST
 *
 * Actions:
 *   - click->attachments-field#browse  (the "Ajouter" button)
 *   - change->attachments-field#change (the hidden input)
 *   - click->attachments-field#remove  (each row's ✕)
 *
 * @example
 *   <div data-controller="attachments-field" data-attachments-field-max-size-value="10">
 *     <button data-action="click->attachments-field#browse">Ajouter</button>
 *     <input type="file" multiple class="sr-only"
 *            data-attachments-field-target="input"
 *            data-action="change->attachments-field#change">
 *     <ul data-attachments-field-target="list"></ul>
 *     <div data-attachments-field-target="empty">Aucune pièce jointe</div>
 *     <p data-attachments-field-target="error" hidden></p>
 *     <template data-attachments-field-target="rowTemplate">…row…</template>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    'input',
    'list',
    'existingList',
    'empty',
    'rowTemplate',
    'error',
    'uploading',
    'uploadingRowTemplate',
  ];
  static values = { maxSize: Number, autoSubmit: Boolean };
  // Optional preview modals (rendered by the component when preview: true).
  static outlets = ['pdf-preview', 'image-preview'];

  connect() {
    // Accumulated set, source of truth. Stable monotonic ids survive removals.
    this.files = [];
    this.nextId = 0;
  }

  // Open the native file picker.
  browse() {
    this.inputTarget.click();
  }

  // Accumulate the latest pick into `this.files`, de-duping and validating.
  change(event) {
    const skipped = [];

    Array.from(event.target.files || []).forEach((file) => {
      if (this._isDuplicate(file)) return;

      const reason = this._rejectionReason(file);
      if (reason) {
        skipped.push(`${file.name} — ${reason}`);
        return;
      }
      this.files.push({ id: this.nextId, file });
      this.nextId += 1;
    });

    this._showSkipped(skipped);
    this._syncInput();
    this._renderRows();

    // Auto-upload mode (planning « Pièces jointes »): submit the moment a valid
    // file is picked, so there is no separate « Enregistrer » step — multi-select
    // accumulates into ONE grouped multipart POST. requestSubmit() snapshots the
    // FileList synchronously, so we reset the staged set right after, which keeps a
    // quick second pick (before the Turbo response replaces the form) from
    // re-submitting the in-flight file as a duplicate. Rejected files leave
    // `this.files` empty, so nothing is submitted — the inline notice explains why.
    if (this.autoSubmitValue && this.files.length > 0) {
      // Capture the names BEFORE the reset so we can show a skeleton row per
      // in-flight file at the end of the list (no "uploading" copy — just the
      // filename + a winking background). They are wiped when the create/422
      // Turbo Stream replaces the whole list container.
      const names = this.files.map((entry) => entry.file.name);
      this.element.closest('form')?.requestSubmit();
      this.files = [];
      this._syncInput();
      this._renderRows();
      this._showUploading(names);
    }
  }

  // Remove one file (by its stable id) and re-sync.
  remove(event) {
    const row = event.currentTarget.closest('[data-file-id]');
    if (!row) return;

    const id = Number(row.dataset.fileId);
    this.files = this.files.filter((entry) => entry.id !== id);
    this._syncInput();
    this._renderRows();
  }

  // Open the file behind a clicked row in the shared PDF/image preview modal.
  // Skips clicks that came from the row's own controls (the trash button).
  preview(event) {
    if (event.target.closest('button, a, input, label')) return;

    const id = Number(event.currentTarget.dataset.fileId);
    const entry = this.files.find((e) => e.id === id);
    if (!entry) return;

    const outlet = this._previewOutletForName(entry.file.name, entry.file.type);
    // Create the blob URL only once we know it will be used; the preview
    // controller tracks it and revokes it on close.
    if (outlet) outlet.openWith(URL.createObjectURL(entry.file), entry.file.name);
  }

  // Enter / Space on a focused row opens the preview (rows are role=button).
  previewKeyboard(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.preview(event);
  }

  // Open an already-attached file (server url) in the shared preview modal.
  // Skips clicks from the row's own controls (the trash button).
  previewExisting(event) {
    if (event.target.closest('button, a, input, label')) return;

    const { url, name } = event.currentTarget.dataset;
    if (!url || url === '#') return; // mock rows have no real file behind '#'

    const outlet = this._previewOutletForName(name || '');
    if (outlet) outlet.openWith(url, name);
  }

  previewExistingKeyboard(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.previewExisting(event);
  }

  // Remove an already-attached file row. Mock: drop the DOM row (the real DELETE
  // is wired in the live slice), then refresh the empty-state.
  removeExisting(event) {
    event.currentTarget.closest('li')?.remove();
    this._refreshEmpty();
  }

  // --- internals ---------------------------------------------------------

  // The preview outlet matching a filename (+ optional MIME), or null when not
  // previewable (or the matching modal isn't wired).
  _previewOutletForName(name, type = '') {
    const lower = name.toLowerCase();
    const isPdf = type === 'application/pdf' || lower.endsWith('.pdf');
    const isImage = type.startsWith('image/') || /\.(jpe?g|png|gif|webp|avif)$/.test(lower);

    if (isPdf && this.hasPdfPreviewOutlet) return this.pdfPreviewOutlet;
    if (isImage && this.hasImagePreviewOutlet) return this.imagePreviewOutlet;
    return null;
  }

  // Rebuild the input's FileList from `this.files` so the form submits the set.
  // (Assigning `.files` programmatically does NOT re-fire `change`, so no loop.)
  _syncInput() {
    const dataTransfer = new DataTransfer();
    this.files.forEach((entry) => dataTransfer.items.add(entry.file));
    this.inputTarget.files = dataTransfer.files;
  }

  _renderRows() {
    this.listTarget.replaceChildren();

    this.files.forEach((entry) => {
      const fragment = this.rowTemplateTarget.content.cloneNode(true);
      const row = fragment.querySelector('li');
      row.dataset.fileId = entry.id;
      row.querySelector('[data-role="name"]').textContent = entry.file.name;
      this.listTarget.appendChild(fragment);
    });

    this.listTarget.classList.toggle('hidden', this.files.length === 0);
    this._refreshEmpty();
  }

  // Render one skeleton row per in-flight file at the END of the list (auto-submit
  // mode). Cleared and replaced wholesale when the server's Turbo Stream swaps the
  // list container, so there's nothing to tear down here.
  _showUploading(names) {
    if (!this.hasUploadingTarget || !this.hasUploadingRowTemplateTarget) return;

    this.uploadingTarget.replaceChildren();
    names.forEach((name) => {
      const fragment = this.uploadingRowTemplateTarget.content.cloneNode(true);
      fragment.querySelector('[data-role="name"]').textContent = name;
      this.uploadingTarget.appendChild(fragment);
    });

    this.uploadingTarget.classList.toggle('hidden', names.length === 0);
    this._refreshEmpty();
  }

  // Show the empty-state only when there are NO files at all (existing + staged +
  // in-flight skeletons).
  _refreshEmpty() {
    if (!this.hasEmptyTarget) return;

    const existing = this.hasExistingListTarget ? this.existingListTarget.children.length : 0;
    const uploading = this.hasUploadingTarget ? this.uploadingTarget.children.length : 0;
    this.emptyTarget.classList.toggle(
      'hidden',
      this.files.length > 0 || existing > 0 || uploading > 0
    );
  }

  _isDuplicate(file) {
    return this.files.some(
      (entry) =>
        entry.file.name === file.name &&
        entry.file.size === file.size &&
        entry.file.lastModified === file.lastModified
    );
  }

  // Returns a French rejection reason, or null when the file is acceptable.
  _rejectionReason(file) {
    if (this.maxSizeValue > 0 && file.size > this.maxSizeValue * 1024 * 1024) {
      return `trop volumineux (max ${this.maxSizeValue} Mo)`;
    }
    if (!this._isAccepted(file)) return 'format non supporté';
    return null;
  }

  _isAccepted(file) {
    const accept = (this.inputTarget.getAttribute('accept') || '').trim();
    if (!accept) return true;

    const name = file.name.toLowerCase();
    return accept
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .some((token) => {
        if (token.startsWith('.')) return name.endsWith(token);
        if (token.endsWith('/*')) return file.type.startsWith(token.slice(0, -1));
        return file.type === token;
      });
  }

  _showSkipped(skipped) {
    if (!this.hasErrorTarget) return;

    if (skipped.length === 0) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
      return;
    }
    this.errorTarget.textContent = skipped.join(' · ');
    this.errorTarget.classList.remove('hidden');
  }
}
